import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Bot, Users, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (ts) => new Date(ts).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
});

export default function LeadsPage() {
  const [chatLeads, setChatLeads] = useState([]);
  const [partnerApps, setPartnerApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [chatRes, partnerRes] = await Promise.all([
          axios.get(`${API}/api/chat-leads`),
          axios.get(`${API}/api/partner-applications`)
        ]);
        setChatLeads(chatRes.data);
        setPartnerApps(partnerRes.data);
      } catch (err) {
        console.error('Failed to load leads', err);
      }
      setLoading(false);
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="pt-32 pb-20 container mx-auto px-4 max-w-6xl">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
          My <span className="text-emerald-600">Leads</span>
        </h1>
        <p className="text-slate-600 mb-10">All enquiries captured from the Yardy chatbot and partner forms</p>

        {loading && <p className="text-slate-500" data-testid="leads-loading">Loading leads...</p>}

        {!loading && (
          <div className="space-y-12">
            <section data-testid="chat-leads-section">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5 text-emerald-600" /> Yardy Chatbot Leads ({chatLeads.length})
              </h2>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-left text-slate-600">
                      <th className="px-4 py-3 font-semibold">Date</th>
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Mobile</th>
                      <th className="px-4 py-3 font-semibold">Requirement</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chatLeads.length === 0 && (
                      <tr><td colSpan="6" className="px-4 py-6 text-center text-slate-400">No chatbot leads yet</td></tr>
                    )}
                    {chatLeads.map((lead) => (
                      <tr key={lead.id} className="border-t border-slate-100 hover:bg-emerald-50/40">
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(lead.timestamp)}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{lead.name}</td>
                        <td className="px-4 py-3 text-slate-700">{lead.mobile}</td>
                        <td className="px-4 py-3 text-slate-700">{lead.requirement}</td>
                        <td className="px-4 py-3 text-slate-700">{lead.loan_amount || '—'}</td>
                        <td className="px-4 py-3">
                          <a href={`tel:${lead.mobile}`} className="inline-flex items-center gap-1 text-emerald-600 font-semibold hover:underline">
                            <Phone className="w-4 h-4" /> Call
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section data-testid="partner-apps-section">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" /> Partner Applications ({partnerApps.length})
              </h2>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-left text-slate-600">
                      <th className="px-4 py-3 font-semibold">Date</th>
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Mobile</th>
                      <th className="px-4 py-3 font-semibold">Profession</th>
                      <th className="px-4 py-3 font-semibold">City</th>
                      <th className="px-4 py-3 font-semibold">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerApps.length === 0 && (
                      <tr><td colSpan="6" className="px-4 py-6 text-center text-slate-400">No partner applications yet</td></tr>
                    )}
                    {partnerApps.map((app, idx) => (
                      <tr key={app.id || `app-${idx}`} className="border-t border-slate-100 hover:bg-emerald-50/40">
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{app.timestamp ? formatDate(app.timestamp) : '—'}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{app.name}</td>
                        <td className="px-4 py-3 text-slate-700">{app.mobile}</td>
                        <td className="px-4 py-3 text-slate-700">{app.profession}</td>
                        <td className="px-4 py-3 text-slate-700">{app.city}</td>
                        <td className="px-4 py-3 text-slate-700">{app.state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
